import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  getFinancialSources,
  createFinancialSource,
  updateFinancialSource,
  deleteFinancialSource,
  markSourcePulled,
  getBankConnections,
  createBankConnectToken,
  syncBankConnection,
  disconnectBankConnection,
  pushBankToLedger,
  registerInCarpcoOs,
  setCarpcoOsClientId,
  searchCarpcoOsClients,
  getTellerAccountOptions,
  setTellerAccount,
  addTellerAccount,
  type FinancialSource,
  type BankConnection,
  type TellerAccountOption,
} from "@/lib/api";
import { useAuth } from "@/lib/authContext";
import { BankTransactionFeed } from "./BankTransactionFeed";
import { format } from "date-fns";
import {
  Plus,
  RefreshCw,
  Edit2,
  Trash2,
  CheckCircle2,
  Clock,
  AlertCircle,
  Building2,
  Link2,
  Wifi,
  WifiOff,
  ChevronDown,
  ChevronRight,
  Upload,
  Settings2,
} from "lucide-react";

const SOURCE_TYPES = [
  { value: "bank", label: "Bank Account" },
  { value: "credit_card", label: "Credit Card" },
  { value: "investment", label: "Investment Account" },
  { value: "loan", label: "Loan / Liability" },
  { value: "payroll", label: "Payroll" },
  { value: "other", label: "Other" },
];

const FREQUENCIES = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annually", label: "Annually" },
];

const STATUS_CONFIG: Record<string, { label: string; className: string; Icon: React.ComponentType<{ className?: string }> }> = {
  ok: { label: "OK", className: "bg-green-50 text-green-700 border-green-200", Icon: CheckCircle2 },
  attention: { label: "Attention", className: "bg-amber-50 text-amber-700 border-amber-200", Icon: Clock },
  overdue: { label: "Overdue", className: "bg-red-50 text-red-700 border-red-200", Icon: AlertCircle },
};

const EMPTY_FORM = {
  source_type: "bank",
  institution_name: "",
  account_nickname: "",
  external_hint: "",
  expected_frequency: "monthly",
  status: "ok" as "ok" | "attention" | "overdue",
  notes: "",
  statement_day: "" as string | number,
  login_url: "",
};

interface Props {
  accountId: string;
}

export function AccountFinancialSources({ accountId }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // canManage: staff/manager/admin — can connect, switch accounts, disconnect
  const canManage = !!user && user.role !== "client" && user.role !== "contractor";
  // canView: all non-client roles — can see bank feed, sync, view transactions, carpco-os
  const canView = !!user && user.role !== "client";

  const [showAddModal, setShowAddModal] = useState(false);
  const [editSource, setEditSource] = useState<FinancialSource | null>(null);
  const [pullSource, setPullSource] = useState<FinancialSource | null>(null);
  const [deleteSource, setDeleteSource] = useState<FinancialSource | null>(null);
  const [disconnectConn, setDisconnectConn] = useState<BankConnection | null>(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [pulledData, setPulledData] = useState({ through_date: "", note: "" });
  const [expandedFeedSourceId, setExpandedFeedSourceId] = useState<string | null>(null);
  const [syncingConnId, setSyncingConnId] = useState<string | null>(null);
  const [pushingConnId, setPushingConnId] = useState<string | null>(null);
  const [linkingConnId, setLinkingConnId] = useState<string | null>(null);
  const [carpcoIdConn, setCarpcoIdConn] = useState<BankConnection | null>(null);
  const [carpcoSearch, setCarpcoSearch] = useState("");
  const [carpcoResults, setCarpcoResults] = useState<{ id: string; name: string }[]>([]);
  const [carpcoSelected, setCarpcoSelected] = useState<{ id: string; name: string } | null>(null);
  const [carpcoSearching, setCarpcoSearching] = useState(false);
  const [savingCarpcoId, setSavingCarpcoId] = useState(false);
  const [accountPickerConn, setAccountPickerConn] = useState<BankConnection | null>(null);
  const [accountOptions, setAccountOptions] = useState<TellerAccountOption[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  // Multi-account: inline "Track separately" sub-form
  const [addAccountOption, setAddAccountOption] = useState<TellerAccountOption | null>(null);
  const [addAccountSourceId, setAddAccountSourceId] = useState<string>("none");
  const [addingAccount, setAddingAccount] = useState(false);
  // Inline new-source creation inside the account picker
  const [showInlineNewSource, setShowInlineNewSource] = useState(false);
  const [inlineSourceNickname, setInlineSourceNickname] = useState("");
  const [inlineSourceType, setInlineSourceType] = useState("bank");
  const [creatingInlineSource, setCreatingInlineSource] = useState(false);

  const { data: sources = [], isLoading } = useQuery({
    queryKey: ["financial-sources", accountId],
    queryFn: () => getFinancialSources(accountId),
    enabled: !!accountId,
  });

  const { data: bankConnections = [] } = useQuery({
    queryKey: ["bank-connections", accountId],
    queryFn: () => getBankConnections(accountId),
    enabled: !!accountId && canView,
  });

  // financial_source_id → active BankConnection
  const connBySourceId = new Map<string, BankConnection>();
  for (const c of bankConnections) {
    if (c.status === "active" && c.financial_source_id) {
      connBySourceId.set(c.financial_source_id, c);
    }
  }

  // teller_account_id → active BankConnection (for tracking in account picker)
  const connByTellerAccountId = new Map<string, BankConnection>();
  for (const c of bankConnections) {
    if (c.status === "active") {
      connByTellerAccountId.set(c.teller_account_id, c);
    }
  }

  // Sources that don't already have an active connection (for "Track separately" source picker)
  const unconnectedSources = sources.filter((s) => !connBySourceId.has(s.id));

  const invalidateSources = () => queryClient.invalidateQueries({ queryKey: ["financial-sources", accountId] });
  const invalidateConns = () => queryClient.invalidateQueries({ queryKey: ["bank-connections", accountId] });

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) =>
      createFinancialSource({
        ...data,
        account_id: accountId,
        statement_day: data.statement_day !== "" ? Number(data.statement_day) : null,
      }),
    onSuccess: () => {
      invalidateSources();
      setShowAddModal(false);
      setFormData({ ...EMPTY_FORM });
      toast({ title: "Source added" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: typeof formData }) =>
      updateFinancialSource(id, {
        ...data,
        statement_day: data.statement_day !== "" ? Number(data.statement_day) : null,
      }),
    onSuccess: () => {
      invalidateSources();
      setEditSource(null);
      toast({ title: "Source updated" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteFinancialSource(id),
    onSuccess: () => {
      invalidateSources();
      setDeleteSource(null);
      toast({ title: "Source deleted" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const markPulledMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: typeof pulledData }) =>
      markSourcePulled(id, data),
    onSuccess: () => {
      invalidateSources();
      setPullSource(null);
      setPulledData({ through_date: "", note: "" });
      toast({ title: "Marked as pulled" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const disconnectMutation = useMutation({
    mutationFn: (id: string) => disconnectBankConnection(id),
    onSuccess: () => {
      invalidateConns();
      setDisconnectConn(null);
      toast({ title: "Disconnected" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const openEdit = (s: FinancialSource) => {
    setFormData({
      source_type: s.source_type,
      institution_name: s.institution_name,
      account_nickname: s.account_nickname ?? "",
      external_hint: s.external_hint ?? "",
      expected_frequency: s.expected_frequency,
      status: (s.status as "ok" | "attention" | "overdue") ?? "ok",
      notes: s.notes ?? "",
      statement_day: s.statement_day ?? "",
      login_url: s.login_url ?? "",
    });
    setEditSource(s);
  };

  async function handleRequestConnect(sourceId: string) {
    try {
      const result = await createBankConnectToken(accountId, sourceId);
      await navigator.clipboard.writeText(result.url);
      toast({ title: "Link copied", description: "Send this link to the client to connect their bank account." });
    } catch {
      toast({ title: "Error", description: "Failed to generate link.", variant: "destructive" });
    }
  }

  async function handleSync(conn: BankConnection, sourceId?: string) {
    setSyncingConnId(conn.id);
    try {
      await syncBankConnection(conn.id);
      invalidateConns();
      queryClient.invalidateQueries({ queryKey: ["bank-transactions", conn.id] });
      toast({ title: "Synced", description: "Transactions updated." });
      if (sourceId) setExpandedFeedSourceId(sourceId);
    } catch {
      toast({ title: "Sync failed", variant: "destructive" });
    } finally {
      setSyncingConnId(null);
    }
  }

  async function handlePushToLedger(conn: BankConnection) {
    setPushingConnId(conn.id);
    try {
      const result = await pushBankToLedger(conn.id);
      toast({ title: "Pushed to Ledger", description: `${result.pushed} transaction(s) sent to carpco-os.` });
    } catch (err: any) {
      toast({ title: "Push failed", description: err.message, variant: "destructive" });
    } finally {
      setPushingConnId(null);
    }
  }

  async function handleRegisterInCarpcoOs(conn: BankConnection) {
    setLinkingConnId(conn.id);
    try {
      const result = await registerInCarpcoOs(conn.id);
      toast({
        title: "Linked to carpco-os",
        description: result.accountName
          ? `"${result.accountName}" registered. Sync is now available in carpco-os Bank Ledger.`
          : "Connection registered. Sync is now available in carpco-os Bank Ledger.",
      });
    } catch (err: any) {
      toast({ title: "Link failed", description: err.message, variant: "destructive" });
    } finally {
      setLinkingConnId(null);
    }
  }

  async function handleCarpcoSearch(q: string) {
    setCarpcoSearch(q);
    setCarpcoSelected(null);
    if (q.length < 2) { setCarpcoResults([]); return; }
    setCarpcoSearching(true);
    try {
      const results = await searchCarpcoOsClients(q);
      setCarpcoResults(results);
    } catch {
      setCarpcoResults([]);
    } finally {
      setCarpcoSearching(false);
    }
  }

  async function handleSaveCarpcoId() {
    if (!carpcoIdConn || !carpcoSelected) return;
    setSavingCarpcoId(true);
    try {
      await setCarpcoOsClientId(carpcoIdConn.id, carpcoSelected.id);
      invalidateConns();
      setCarpcoIdConn(null);
      setCarpcoSearch("");
      setCarpcoResults([]);
      setCarpcoSelected(null);
      toast({ title: `Linked to "${carpcoSelected.name}"`, description: "Link to carpco-os and Push ledger are now available." });
    } catch (err: any) {
      toast({ title: "Save failed", description: err.message, variant: "destructive" });
    } finally {
      setSavingCarpcoId(false);
    }
  }

  async function handleOpenAccountPicker(conn: BankConnection) {
    setLoadingAccounts(true);
    setAccountPickerConn(conn);
    setAddAccountOption(null);
    setAddAccountSourceId("none");
    try {
      const opts = await getTellerAccountOptions(conn.id);
      setAccountOptions(opts);
    } catch {
      toast({ title: "Failed to load accounts", variant: "destructive" });
      setAccountPickerConn(null);
    } finally {
      setLoadingAccounts(false);
    }
  }

  async function handleSelectAccount(tellerAccountId: string) {
    if (!accountPickerConn) return;
    try {
      await setTellerAccount(accountPickerConn.id, tellerAccountId);
      invalidateConns();
      setAccountPickerConn(null);
      toast({ title: "Account updated", description: "Sync now to pull transactions." });
    } catch {
      toast({ title: "Failed to update account", variant: "destructive" });
    }
  }

  async function handleAddAccount() {
    if (!accountPickerConn || !addAccountOption) return;
    setAddingAccount(true);
    try {
      await addTellerAccount(accountPickerConn.id, addAccountOption.id, addAccountSourceId !== "none" ? addAccountSourceId : undefined);
      invalidateConns();
      setAccountPickerConn(null);
      setAddAccountOption(null);
      setAddAccountSourceId("none");
      toast({ title: "Account added", description: "Sync now to pull transactions for the new account." });
    } catch (err: any) {
      toast({ title: "Failed to add account", description: err.message, variant: "destructive" });
    } finally {
      setAddingAccount(false);
    }
  }

  async function handleCreateInlineSource() {
    if (!accountPickerConn || !inlineSourceNickname.trim()) return;
    setCreatingInlineSource(true);
    try {
      const newSource = await createFinancialSource({
        account_id: accountId,
        institution_name: accountPickerConn.teller_institution || inlineSourceNickname.trim(),
        account_nickname: inlineSourceNickname.trim(),
        source_type: inlineSourceType,
        expected_frequency: "monthly",
        status: "ok",
      });
      invalidateSources();
      setAddAccountSourceId(newSource.id);
      setShowInlineNewSource(false);
      setInlineSourceNickname("");
      setInlineSourceType("bank");
    } catch (err: any) {
      toast({ title: "Failed to create source", description: err.message, variant: "destructive" });
    } finally {
      setCreatingInlineSource(false);
    }
  }

  // Group sources by institution name
  const grouped = sources.reduce<Record<string, FinancialSource[]>>((acc, s) => {
    const key = s.institution_name;
    if (!acc[key]) acc[key] = [];
    acc[key].push(s);
    return acc;
  }, {});

  const sourceTypeLabel = (v: string) => SOURCE_TYPES.find((t) => t.value === v)?.label ?? v;

  return (
    <div className="rounded-lg border bg-card shadow-sm">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <span className="text-sm font-bold text-foreground tracking-tight">Financial Sources</span>
        <Button size="sm" variant="outline" onClick={() => { setFormData({ ...EMPTY_FORM }); setShowAddModal(true); }}>
          <Plus className="w-3.5 h-3.5 mr-1.5" />
          Add Source
        </Button>
      </div>

      {isLoading ? (
        <div className="py-8 text-center text-sm text-muted-foreground">Loading sources…</div>
      ) : sources.length === 0 ? (
        <div className="py-8 text-center text-sm text-muted-foreground">
          No financial sources yet. Add banks, credit cards, or other accounts to track statement pulls.
        </div>
      ) : (
        <div className="divide-y">
          {Object.entries(grouped).map(([institution, rows]) => (
            <div key={institution}>
              <div className="flex items-center gap-2 px-4 py-2 bg-muted/30">
                <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{institution}</span>
              </div>
              {rows.map((s) => {
                const st = STATUS_CONFIG[s.status] ?? STATUS_CONFIG.ok;
                const conn = canView ? connBySourceId.get(s.id) : undefined;
                const isFeedOpen = expandedFeedSourceId === s.id;
                const isSyncing = syncingConnId === conn?.id;
                const isPushing = pushingConnId === conn?.id;
                const isLinking = linkingConnId === conn?.id;
                return (
                  <div key={s.id} className="group">
                    {/* ── Main info row ── */}
                    <div className="flex items-start gap-3 px-4 py-2.5 hover:bg-muted/20 transition-colors">
                      <div className="flex-1 min-w-0">
                        {/* Name + hint */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium">
                            {s.account_nickname || sourceTypeLabel(s.source_type)}
                          </span>
                          {s.external_hint && (
                            <span className="text-xs text-muted-foreground">{s.external_hint}</span>
                          )}
                        </div>
                        {/* Badges — always on their own line so OK + Connected stay together */}
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <Badge variant="outline" className={`text-xs ${st.className}`}>
                            <st.Icon className="w-3 h-3 mr-1" />
                            {st.label}
                          </Badge>
                          {conn && (
                            <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-300 flex items-center gap-1">
                              <Wifi className="w-3 h-3" />
                              Connected
                              {conn.balance != null && (
                                <span className="ml-1 font-semibold">
                                  · {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(parseFloat(conn.balance))}
                                </span>
                              )}
                            </Badge>
                          )}
                        </div>
                        {/* Metadata */}
                        <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground flex-wrap">
                          <span>{sourceTypeLabel(s.source_type)}</span>
                          <span>·</span>
                          <span>{FREQUENCIES.find((f) => f.value === s.expected_frequency)?.label ?? s.expected_frequency}</span>
                          {s.statement_day && (
                            <>
                              <span>·</span>
                              <span>Drops day {s.statement_day}</span>
                            </>
                          )}
                          {s.last_pulled_date && (
                            <>
                              <span>·</span>
                              <span>Last pulled: {format(new Date(s.last_pulled_date + "T12:00:00"), "MM/dd/yy")}</span>
                            </>
                          )}
                          {s.through_date && (
                            <>
                              <span>·</span>
                              <span>Through: {format(new Date(s.through_date + "T12:00:00"), "MM/dd/yy")}</span>
                            </>
                          )}
                          {conn?.last_synced_at && (
                            <>
                              <span>·</span>
                              <span>Synced: {format(new Date(conn.last_synced_at), "MM/dd/yy h:mm a")}</span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Standard actions — always on the right */}
                      <div className="flex items-center gap-1 shrink-0">
                        {canManage && !conn && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-blue-600/70 hover:text-blue-600"
                            onClick={() => handleRequestConnect(s.id)}
                            title="Request bank connection (copies link to clipboard)"
                          >
                            <Link2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-muted-foreground"
                          onClick={() => { setPulledData({ through_date: "", note: "" }); setPullSource(s); }}
                          title="Mark as pulled"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(s)} title="Edit">
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-destructive/70 hover:text-destructive"
                          onClick={() => setDeleteSource(s)}
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>

                    {/* ── Bank controls strip (all staff + connected) ── */}
                    {canView && conn && (
                      <div className="flex items-center flex-wrap gap-x-4 gap-y-1 px-4 py-1.5 bg-green-50/40 border-t text-xs">
                        <button
                          type="button"
                          className={`flex items-center gap-1 font-medium text-green-700 hover:text-green-900 transition-colors ${isSyncing ? "opacity-60 cursor-not-allowed" : ""}`}
                          onClick={() => !isSyncing && handleSync(conn, s.id)}
                          disabled={isSyncing}
                        >
                          <RefreshCw className={`w-3 h-3 ${isSyncing ? "animate-spin" : ""}`} />
                          {isSyncing ? "Syncing…" : "Sync"}
                        </button>

                        <button
                          type="button"
                          className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
                          onClick={() => setExpandedFeedSourceId(isFeedOpen ? null : s.id)}
                        >
                          {isFeedOpen
                            ? <ChevronDown className="w-3 h-3" />
                            : <ChevronRight className="w-3 h-3" />}
                          Transactions
                        </button>

                        {isAdmin && (
                          <>
                            <span className="text-muted-foreground/30 select-none">|</span>
                            {conn.carpco_os_client_id ? (
                              <>
                                <button
                                  type="button"
                                  className={`flex items-center gap-1 text-indigo-600 hover:text-indigo-800 transition-colors ${isLinking ? "opacity-60 cursor-not-allowed" : ""}`}
                                  onClick={() => !isLinking && handleRegisterInCarpcoOs(conn)}
                                  disabled={isLinking}
                                >
                                  <Link2 className={`w-3 h-3 ${isLinking ? "animate-pulse" : ""}`} />
                                  {isLinking ? "Linking…" : "Link to carpco-os"}
                                </button>
                                <button
                                  type="button"
                                  className={`flex items-center gap-1 text-violet-700 hover:text-violet-900 transition-colors ${isPushing ? "opacity-60 cursor-not-allowed" : ""}`}
                                  onClick={() => !isPushing && handlePushToLedger(conn)}
                                  disabled={isPushing}
                                >
                                  <Upload className={`w-3 h-3 ${isPushing ? "animate-pulse" : ""}`} />
                                  {isPushing ? "Pushing…" : "Push ledger"}
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                className="flex items-center gap-1 text-slate-400 hover:text-indigo-600 transition-colors"
                                onClick={() => { setCarpcoIdConn(conn); setCarpcoSearch(""); setCarpcoResults([]); setCarpcoSelected(null); }}
                                title="Link to a carpco-os client to enable Push"
                              >
                                <Link2 className="w-3 h-3" />
                                Set carpco-os ID
                              </button>
                            )}
                          </>
                        )}

                        <span className="flex-1" />

                        {canManage && (
                          <>
                            <button
                              type="button"
                              className="flex items-center gap-1 text-muted-foreground hover:text-slate-700 transition-colors"
                              onClick={() => handleOpenAccountPicker(conn)}
                            >
                              <Settings2 className="w-3 h-3" />
                              Accounts
                            </button>

                            <button
                              type="button"
                              className="flex items-center gap-1 text-amber-600/70 hover:text-amber-600 transition-colors"
                              onClick={() => setDisconnectConn(conn)}
                            >
                              <WifiOff className="w-3 h-3" />
                              Disconnect
                            </button>
                          </>
                        )}
                      </div>
                    )}

                    {/* ── Transaction feed ── */}
                    {canView && conn && isFeedOpen && (
                      <div className="px-4 pb-3 border-t bg-muted/10">
                        <BankTransactionFeed
                          connectionId={conn.id}
                          storedBalance={conn.balance ?? null}
                          storedBalanceAsOf={conn.balance_as_of ?? null}
                          onSync={() => handleSync(conn, s.id)}
                          syncing={isSyncing}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* ── Add / Edit modal ── */}
      <Dialog
        open={showAddModal || !!editSource}
        onOpenChange={(open) => { if (!open) { setShowAddModal(false); setEditSource(null); } }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editSource ? "Edit Source" : "Add Financial Source"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Type</Label>
                <Select value={formData.source_type} onValueChange={(v) => setFormData((f) => ({ ...f, source_type: v }))}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SOURCE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Frequency</Label>
                <Select value={formData.expected_frequency} onValueChange={(v) => setFormData((f) => ({ ...f, expected_frequency: v }))}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FREQUENCIES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Institution Name <span className="text-destructive">*</span></Label>
              <Input
                className="h-9 text-sm"
                placeholder="e.g. Chase, Wells Fargo"
                value={formData.institution_name}
                onChange={(e) => setFormData((f) => ({ ...f, institution_name: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Account Nickname</Label>
                <Input className="h-9 text-sm" placeholder="e.g. Business Checking" value={formData.account_nickname} onChange={(e) => setFormData((f) => ({ ...f, account_nickname: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Hint (last 4, etc.)</Label>
                <Input className="h-9 text-sm" placeholder="e.g. x1234" value={formData.external_hint} onChange={(e) => setFormData((f) => ({ ...f, external_hint: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Statement Drop Day</Label>
                <Input
                  className="h-9 text-sm"
                  type="number"
                  min={1}
                  max={31}
                  placeholder="e.g. 15"
                  value={formData.statement_day}
                  onChange={(e) => setFormData((f) => ({ ...f, statement_day: e.target.value ? parseInt(e.target.value) : "" }))}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Login URL</Label>
                <Input
                  className="h-9 text-sm"
                  placeholder="https://..."
                  value={formData.login_url}
                  onChange={(e) => setFormData((f) => ({ ...f, login_url: e.target.value }))}
                />
              </div>
            </div>
            {editSource && (
              <div className="space-y-1">
                <Label className="text-xs">Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData((f) => ({ ...f, status: v as "ok" | "attention" | "overdue" }))}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ok">OK</SelectItem>
                    <SelectItem value="attention">Attention</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs">Notes</Label>
              <Textarea className="text-sm resize-none" rows={2} value={formData.notes} onChange={(e) => setFormData((f) => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddModal(false); setEditSource(null); }}>Cancel</Button>
            <Button
              disabled={!formData.institution_name.trim() || createMutation.isPending || updateMutation.isPending}
              onClick={() => {
                if (editSource) {
                  updateMutation.mutate({ id: editSource.id, data: formData });
                } else {
                  createMutation.mutate(formData);
                }
              }}
            >
              {editSource ? "Save" : "Add Source"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Mark Pulled modal ── */}
      <Dialog open={!!pullSource} onOpenChange={(open) => { if (!open) setPullSource(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Mark as Pulled</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">
            {pullSource?.institution_name}{pullSource?.account_nickname ? ` — ${pullSource.account_nickname}` : ""}
          </p>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Statement Through Date</Label>
              <Input type="date" className="h-9 text-sm" value={pulledData.through_date} onChange={(e) => setPulledData((d) => ({ ...d, through_date: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Note (optional)</Label>
              <Input className="h-9 text-sm" placeholder="e.g. Downloaded Jan 2025 statement" value={pulledData.note} onChange={(e) => setPulledData((d) => ({ ...d, note: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPullSource(null)}>Cancel</Button>
            <Button
              disabled={markPulledMutation.isPending}
              onClick={() => pullSource && markPulledMutation.mutate({ id: pullSource.id, data: pulledData })}
            >
              Mark Pulled
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete confirmation ── */}
      <Dialog open={!!deleteSource} onOpenChange={(open) => { if (!open) setDeleteSource(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Source?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Delete <strong>{deleteSource?.institution_name}</strong>
            {deleteSource?.account_nickname ? ` — ${deleteSource.account_nickname}` : ""}? This will also delete its pull history. This cannot be undone.
          </p>
          {deleteSource && connBySourceId.has(deleteSource.id) && (
            <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
              This source has an active bank connection. Deleting it will automatically disconnect the bank sync — no new transactions will pull. Previously synced transactions are kept.
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteSource(null)}>Cancel</Button>
            <Button variant="destructive" disabled={deleteMutation.isPending} onClick={() => deleteSource && deleteMutation.mutate(deleteSource.id)}>
              Delete{deleteSource && connBySourceId.has(deleteSource.id) ? " & Disconnect" : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Account picker / multi-account manager (non-contractor only) ── */}
      {canManage && (
        <Dialog
          open={!!accountPickerConn}
          onOpenChange={(open) => {
            if (!open) {
              setAccountPickerConn(null);
              setAddAccountOption(null);
              setAddAccountSourceId("none");
              setShowInlineNewSource(false);
              setInlineSourceNickname("");
              setInlineSourceType("bank");
            }
          }}
        >
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Teller Accounts</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground -mt-2">
              Switch which account this connection syncs, or track additional accounts from the same bank login.
            </p>
            {loadingAccounts ? (
              <div className="py-4 text-center text-sm text-muted-foreground">
                <RefreshCw className="w-4 h-4 animate-spin inline mr-1" /> Loading accounts…
              </div>
            ) : (
              <div className="space-y-2">
                {accountOptions.map((a) => {
                  const trackedConn = connByTellerAccountId.get(a.id);
                  const isTrackedElsewhere = !!trackedConn && !a.current;
                  const trackedSourceName = trackedConn?.financial_source_id
                    ? (sources.find((s) => s.id === trackedConn.financial_source_id)?.account_nickname
                      ?? sources.find((s) => s.id === trackedConn.financial_source_id)?.institution_name
                      ?? null)
                    : null;
                  const isExpanded = addAccountOption?.id === a.id;

                  return (
                    <div
                      key={a.id}
                      className={`rounded-md border text-sm overflow-hidden ${a.current ? "border-green-400 bg-green-50" : "border-border"}`}
                    >
                      <div className="flex items-center gap-2 px-3 py-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium">{a.name}{a.last_four ? ` ···${a.last_four}` : ""}</div>
                          <div className="text-xs text-muted-foreground capitalize">{a.subtype.replace(/_/g, " ")}</div>
                        </div>
                        {a.current ? (
                          <span className="text-xs text-green-700 font-medium shrink-0">Syncing</span>
                        ) : isTrackedElsewhere ? (
                          <span className="text-xs text-blue-600 shrink-0">
                            Tracked{trackedSourceName ? ` — ${trackedSourceName}` : ""}
                          </span>
                        ) : (
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              className="text-xs border rounded px-2 py-0.5 text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
                              onClick={() => handleSelectAccount(a.id)}
                            >
                              Switch sync
                            </button>
                            <button
                              type="button"
                              className="text-xs border border-blue-300 rounded px-2 py-0.5 text-blue-600 hover:bg-blue-50 transition-colors"
                              onClick={() => {
                                setAddAccountOption(isExpanded ? null : a);
                                setAddAccountSourceId("none");
                              }}
                            >
                              {isExpanded ? "Cancel" : "Track separately"}
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Inline sub-form for "Track separately" */}
                      {isExpanded && (
                        <div className="border-t px-3 py-2.5 bg-muted/20 space-y-2">
                          <p className="text-xs text-muted-foreground">
                            Link to a financial source so transactions appear under that account:
                          </p>
                          <Select value={addAccountSourceId} onValueChange={(v) => { setAddAccountSourceId(v); setShowInlineNewSource(false); }}>
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue placeholder="— No source (unlinked) —" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">— No source (unlinked) —</SelectItem>
                              {unconnectedSources.map((s) => (
                                <SelectItem key={s.id} value={s.id}>
                                  {s.account_nickname || s.institution_name}
                                  {s.external_hint ? ` (${s.external_hint})` : ""}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          {/* On-the-fly source creation */}
                          {!showInlineNewSource ? (
                            <button
                              type="button"
                              className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                              onClick={() => setShowInlineNewSource(true)}
                            >
                              + Create new source
                            </button>
                          ) : (
                            <div className="space-y-1.5 pt-1 border-t">
                              <p className="text-xs text-muted-foreground font-medium">New source</p>
                              <div className="flex gap-1.5">
                                <Select value={inlineSourceType} onValueChange={setInlineSourceType}>
                                  <SelectTrigger className="h-7 text-xs w-36 shrink-0">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {SOURCE_TYPES.map((t) => (
                                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <Input
                                  className="h-7 text-xs"
                                  placeholder="Nickname (e.g. Business Savings)"
                                  value={inlineSourceNickname}
                                  onChange={(e) => setInlineSourceNickname(e.target.value)}
                                  onKeyDown={(e) => e.key === "Enter" && handleCreateInlineSource()}
                                  autoFocus
                                />
                              </div>
                              <div className="flex gap-1.5">
                                <Button
                                  size="sm"
                                  className="h-7 text-xs"
                                  onClick={handleCreateInlineSource}
                                  disabled={!inlineSourceNickname.trim() || creatingInlineSource}
                                >
                                  {creatingInlineSource ? "Creating…" : "Create"}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 text-xs"
                                  onClick={() => { setShowInlineNewSource(false); setInlineSourceNickname(""); }}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          )}

                          <div className="flex gap-2 pt-1">
                            <Button
                              size="sm"
                              className="h-7 text-xs"
                              onClick={handleAddAccount}
                              disabled={addingAccount}
                            >
                              {addingAccount ? "Adding…" : "Add connection"}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-xs"
                              onClick={() => { setAddAccountOption(null); setAddAccountSourceId("none"); setShowInlineNewSource(false); }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setAccountPickerConn(null); setAddAccountOption(null); }}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Disconnect confirmation (non-contractor only) ── */}
      {canManage && (
        <Dialog open={!!disconnectConn} onOpenChange={(open) => { if (!open) setDisconnectConn(null); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Disconnect Bank?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Disconnect <strong>{disconnectConn?.teller_institution ?? "this bank"}</strong>? Existing transactions will be preserved, but no new data will sync.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDisconnectConn(null)}>Cancel</Button>
              <Button
                variant="destructive"
                disabled={disconnectMutation.isPending}
                onClick={() => disconnectConn && disconnectMutation.mutate(disconnectConn.id)}
              >
                Disconnect
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── carpco-os client linker (admin only) ── */}
      {isAdmin && (
        <Dialog open={!!carpcoIdConn} onOpenChange={(open) => { if (!open) { setCarpcoIdConn(null); setCarpcoSearch(""); setCarpcoResults([]); setCarpcoSelected(null); } }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Link to carpco-os Client</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground -mt-2">
              Search for the matching client in carpco-os to enable Push ledger.
            </p>
            <div className="space-y-2">
              <div className="space-y-1">
                <Label className="text-xs">Search by client name</Label>
                <Input
                  className="h-9 text-sm"
                  placeholder="e.g. Arcco Group"
                  value={carpcoSearch}
                  onChange={(e) => handleCarpcoSearch(e.target.value)}
                  autoFocus
                />
              </div>
              {carpcoSearching && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <RefreshCw className="w-3 h-3 animate-spin" /> Searching…
                </p>
              )}
              {carpcoResults.length > 0 && !carpcoSelected && (
                <div className="rounded-md border divide-y">
                  {carpcoResults.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-muted/40 transition-colors"
                      onClick={() => { setCarpcoSelected(r); setCarpcoSearch(r.name); setCarpcoResults([]); }}
                    >
                      {r.name}
                    </button>
                  ))}
                </div>
              )}
              {carpcoSelected && (
                <div className="rounded-md border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-800 flex items-center justify-between">
                  <span>✓ {carpcoSelected.name}</span>
                  <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => { setCarpcoSelected(null); setCarpcoSearch(""); }}>Change</button>
                </div>
              )}
              {!carpcoSearching && carpcoSearch.length >= 2 && carpcoResults.length === 0 && !carpcoSelected && (
                <p className="text-xs text-muted-foreground">No clients found for "{carpcoSearch}"</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setCarpcoIdConn(null); setCarpcoSearch(""); setCarpcoResults([]); setCarpcoSelected(null); }}>Cancel</Button>
              <Button disabled={!carpcoSelected || savingCarpcoId} onClick={handleSaveCarpcoId}>
                {savingCarpcoId ? "Saving…" : "Link"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
